import apiBaseUrl from '../../utils/config.js';
import { authFetch } from '../../utils/authFetch.js';

// Robust response parser: handles empty, JSON, or plain-text bodies
async function parseResponse(res) {
  const text = await res.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

class patientInfoService {
  constructor() {
    this.baseUrl = `${apiBaseUrl}/patientinfo`;
  }

  getToken() {
    return localStorage.getItem('jwt');
  }

  getAuthHeaders(includeJson = true) {
    const token = this.getToken();

    return {
      ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  getJsonHeaders() {
    return {
      'Content-Type': 'application/json',
    };
  }

  getPlainHeaders() {
    return {};
  }

  // =========================
  // Registration
  // =========================
  async registerCredentials(registerRequest) {
    try {
      const t0 = window.__formStart || Date.now();
      const elapsed = Date.now() - t0;

      // Public route: keep normal fetch because no JWT is required
      const response = await fetch(`${this.baseUrl}/register/credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Form-Start': String(t0),
          'X-Page-Elapsed': String(elapsed),
          'X-Honeypot': '',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...registerRequest,
          honeypot: '',
        }),
      });

      const data = await parseResponse(response);

      if (response.ok) {
        return data || {};
      }

      if (
        response.status === 403 &&
        data &&
        data.action === 'captcha_required'
      ) {
        const err = new Error('captcha_required');
        err.status = 403;
        err.data = data;
        throw err;
      }

      if (response.status === 401) {
        const msg = (data && (data.error || data.message)) || 'unauthorized';
        const err = new Error(msg);
        err.status = 401;
        throw err;
      }

      const msg =
        (data && (data.error || data.message)) || `HTTP ${response.status}`;
      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Staff-only patient registration
  async registerPatientByStaff(staffRegisterRequest) {
    try {
      const response = await authFetch(`${this.baseUrl}/staff/register`, {
        method: 'POST',
        headers: this.getJsonHeaders(),
        body: JSON.stringify(staffRegisterRequest),
      });

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg =
          (data && (data.error || data.message)) ||
          'Staff patient registration failed';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || {};
    } catch (error) {
      console.error('Staff patient registration error:', error);
      throw error;
    }
  }

  // =========================
  // Login / Logout
  // =========================
  async login(loginRequest) {
    try {
      // Public route: keep normal fetch because no JWT is required
      const response = await fetch(`${this.baseUrl}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginRequest),
      });

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg = (data && (data.error || data.message)) || 'Login failed';
        throw new Error(msg);
      }

      const result = data || {};

      if (result.token) {
        localStorage.setItem('jwt', result.token);
      }

      if (result.username) {
        localStorage.setItem('username', result.username);
      }

      if (result.patientId) {
        localStorage.setItem('patientId', result.patientId);
      }

      localStorage.setItem('role', result.role || 'ROLE_PATIENT');

      window.dispatchEvent(new Event('userUpdated'));

      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async logout() {
    localStorage.removeItem('jwt');
    localStorage.removeItem('username');
    localStorage.removeItem('patientId');
    localStorage.removeItem('role');

    window.dispatchEvent(new Event('userUpdated'));

    console.log('User logged out');
  }

  // =========================
  // Patient Profile Details
  // =========================
  async savePatientDetails(patientId, username, detailsRequest, bearerOverride) {
    try {
      const qs = new URLSearchParams();

      if (patientId) {
        qs.append('patientId', patientId);
      }

      if (username) {
        qs.append('username', username);
      }

      const profilePayload = {
        firstName: detailsRequest.firstName ?? '',
        lastName: detailsRequest.lastName ?? '',
        phoneNumber: detailsRequest.phoneNumber ?? '',
        email: detailsRequest.email ?? '',
        sex: detailsRequest.sex ?? '',
        dateOfBirth: detailsRequest.dateOfBirth || null,
        notes: detailsRequest.notes ?? '',
        age: detailsRequest.age ?? null,
        visits: detailsRequest.visits ?? null,
        nfWc: detailsRequest.nfWc ?? null,
      };

      const headers = this.getJsonHeaders();

      if (bearerOverride) {
        headers.Authorization = `Bearer ${bearerOverride}`;
      }

      const response = await authFetch(
        `${this.baseUrl}/update-details?${qs.toString()}`,
        {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify(profilePayload),
        }
      );

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg =
          (data && (data.error || data.message)) ||
          'Failed to update patient details';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || {};
    } catch (error) {
      console.error('Update patient details error:', error.message);
      throw error;
    }
  }

  // =========================
  // Patient Account Security
  // =========================
  async updatePatientAccount(patientId, newUsername, newPassword, newEmail) {
    try {
      const accountPayload = {
        username: newUsername,
        password: newPassword || '',
        email: newEmail,
      };

      const response = await authFetch(
        `${this.baseUrl}/update-account?patientId=${encodeURIComponent(
          patientId
        )}`,
        {
          method: 'PUT',
          headers: this.getJsonHeaders(),
          body: JSON.stringify(accountPayload),
        }
      );

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg = (data && (data.error || data.message)) || 'Update failed';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || {};
    } catch (error) {
      console.error('Error updating account:', error.message);
      throw error;
    }
  }

  // =========================
  // Fetch Patient Data
  // =========================
  async getPatientById(patientId) {
    try {
      const response = await authFetch(
        `${this.baseUrl}/${encodeURIComponent(patientId)}`,
        {
          method: 'GET',
          headers: this.getPlainHeaders(),
        }
      );

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg = (data && (data.error || data.message)) || 'Fetch failed';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || {};
    } catch (error) {
      console.error('Fetch patient error:', error);
      throw error;
    }
  }

  async getPatientByUsername(username) {
    try {
      const response = await authFetch(
        `${this.baseUrl}/by-username/${encodeURIComponent(username)}`,
        {
          method: 'GET',
          headers: this.getPlainHeaders(),
        }
      );

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg = (data && (data.error || data.message)) || 'Fetch failed';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || {};
    } catch (error) {
      console.error('Error fetching patient by username:', error);
      throw error;
    }
  }

  async getPatientByUsernameOrEmail(usernameOrEmail) {
    try {
      const response = await authFetch(
        `${this.baseUrl}/by-username-or-email?usernameOrEmail=${encodeURIComponent(
          usernameOrEmail
        )}`,
        {
          method: 'GET',
          headers: this.getPlainHeaders(),
        }
      );

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg = (data && (data.error || data.message)) || 'Fetch failed';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || {};
    } catch (error) {
      console.error('Error fetching patient by username or email:', error);
      throw error;
    }
  }

  // =========================
  // Search Patients
  // =========================
  async searchPatients(firstName, lastName) {
    try {
      const qs = new URLSearchParams();

      if (firstName) {
        qs.append('firstName', firstName);
      }

      if (lastName) {
        qs.append('lastName', lastName);
      }

      const response = await authFetch(
        `${this.baseUrl}/search/first/last?${qs.toString()}`,
        {
          method: 'GET',
          headers: this.getPlainHeaders(),
        }
      );

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg = (data && (data.error || data.message)) || 'Search failed';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || [];
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  }

  async searchPatientsByQuery(query) {
    try {
      const response = await authFetch(
        `${this.baseUrl}/search/all?query=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: this.getPlainHeaders(),
        }
      );

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg = (data && (data.error || data.message)) || 'Search failed';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || [];
    } catch (error) {
      console.error('Search query error:', error);
      throw error;
    }
  }

  async getAllPatients() {
    try {
      const response = await authFetch(`${this.baseUrl}/all`, {
        method: 'GET',
        headers: this.getPlainHeaders(),
      });

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg = (data && (data.error || data.message)) || 'Fetch failed';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching all patients:', error);
      throw error;
    }
  }

  // =========================
  // Delete Patient
  // =========================
  async deletePatientById(patientId) {
    try {
      const response = await authFetch(
        `${this.baseUrl}/${encodeURIComponent(patientId)}`,
        {
          method: 'DELETE',
          headers: this.getPlainHeaders(),
        }
      );

      const data = await parseResponse(response);

      if (!response.ok) {
        const msg =
          (data && (data.error || data.message)) ||
          'Failed to delete patient';

        const err = new Error(msg);
        err.status = response.status;
        err.data = data;
        throw err;
      }

      return data || {};
    } catch (error) {
      console.error('Delete patient error:', error);
      throw error;
    }
  }

  // =========================
  // Password Reset
  // =========================
  async requestResetCode(usernameOrEmail) {
    // Public route: keep normal fetch because no JWT is required
    const response = await fetch(`${this.baseUrl}/request-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ usernameOrEmail }),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      const msg =
        (data && (data.error || data.message)) ||
        'We could not find an account with that username or email address. If you do not remember the email address on file, please contact the office for assistance.';

      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    }

    return data || {};
  }

  async verifyResetCodeAndChangePassword({
    usernameOrEmail,
    username,
    code,
    newPassword,
  }) {
    // Public route: keep normal fetch because no JWT is required
    const response = await fetch(`${this.baseUrl}/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        usernameOrEmail: usernameOrEmail || username,
        code,
        newPassword,
      }),
    });

    const data = await parseResponse(response);

    if (!response.ok) {
      const msg =
        (data && (data.error || data.message)) || 'Failed to reset password';

      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    }

    return data || {};
  }
}

export default patientInfoService;